import { Link } from "react-router-dom";

const LAST_UPDATED = "12 June 2026";
const CONTACT_EMAIL = "vend@thegoodbox.com";
const OPERATOR = "Good Box Limited";

function LegalPage({ title, testid, children }) {
  return (
    <div className="min-h-screen bg-slate-50" data-testid={testid}>
      <header className="bg-[#3730A3] text-white">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <a href="https://vend.thegoodbox.com" className="text-xl font-bold tracking-tight" data-testid="legal-brand">
            The Good Box
          </a>
          <Link to="/admin/login" className="text-sm text-indigo-200 hover:text-white transition-colors" data-testid="legal-back-link">
            Back to sign in →
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">{title}</h1>
        <p className="mt-3 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
        <div className="mt-10 space-y-8 text-slate-700 leading-relaxed text-base">{children}</div>
        <footer className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-500">
          <p>{OPERATOR}. Registered in England &amp; Wales.</p>
          <p className="mt-1">
            Questions? Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#3730A3] font-medium">
              {CONTACT_EMAIL}
            </a>
            .
          </p>
          <div className="mt-4 flex gap-4">
            <Link to="/privacy-policy" className="hover:text-[#3730A3]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#3730A3]">Terms of Service</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}

const H2 = ({ children }) => <h2 className="text-lg font-semibold text-slate-900">{children}</h2>;
const P = ({ children }) => <p>{children}</p>;
const Section = ({ title, children }) => (
  <section className="space-y-3">
    <H2>{title}</H2>
    {children}
  </section>
);

export function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" testid="privacy-policy-page">
      <P>
        This Privacy Policy explains how {OPERATOR} ("The Good Box", "we", "us") collects, uses and protects
        personal data when you use The Good Box self-service kiosks and admin portal at vend.thegoodbox.com
        (the "Service"). We comply with the UK GDPR and the Data Protection Act 2018.
      </P>

      <Section title="1. Information we collect">
        <P>
          <strong>Shoppers (kiosk):</strong> when you buy from a kiosk we process your purchase details (items,
          amounts, VAT and transaction reference) and, if you choose to receive a receipt, the email address you
          provide. Card payments are handled directly by Stripe — we never see or store your full card number.
        </P>
        <P>
          <strong>Administrators:</strong> when you sign in to the admin portal we process your name, email address
          and Google account identifier (via Google Sign-In), your role and permissions, and security data such as
          two-factor status and login timestamps.
        </P>
      </Section>

      <Section title="2. How we use your information">
        <P>
          We use personal data to operate the kiosks and process sales, send purchase receipts you request, provide
          and secure the admin portal, prevent fraud and abuse, meet our accounting and VAT obligations, and send
          essential service and account emails (such as invitations, password resets and low-stock alerts).
        </P>
      </Section>

      <Section title="3. Legal bases">
        <P>
          We rely on <strong>contract</strong> (to complete your purchase and provide the Service),
          <strong> legitimate interests</strong> (to secure and improve the Service and prevent fraud),
          <strong> consent</strong> (where you ask us to email you a receipt), and
          <strong> legal obligation</strong> (to keep financial records).
        </P>
      </Section>

      <Section title="4. Sharing with third parties">
        <P>We share data only with service providers that help us run the Service:</P>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Stripe</strong> — payment processing.</li>
          <li><strong>Google</strong> — administrator sign-in (Google Sign-In).</li>
          <li><strong>Resend</strong> — delivery of transactional emails such as receipts and account emails.</li>
        </ul>
        <P>We do not sell your personal data.</P>
      </Section>

      <Section title="5. Data retention">
        <P>
          Transaction and VAT records are kept for at least six years to meet UK tax law. Administrator account data
          is kept for as long as the account is active and deleted on request thereafter, subject to any legal
          retention requirements.
        </P>
      </Section>

      <Section title="6. Your rights">
        <P>
          You have the right to access, correct, delete or restrict processing of your personal data, to object to
          processing, and to data portability. To exercise these rights, contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#3730A3] font-medium">{CONTACT_EMAIL}</a>. You may also
          complain to the UK Information Commissioner's Office (ico.org.uk).
        </P>
      </Section>

      <Section title="7. Security">
        <P>
          We protect data with encryption in transit, access controls, hashed credentials, two-factor authentication
          for administrators and rate limiting against abuse.
        </P>
      </Section>

      <Section title="8. Changes to this policy">
        <P>
          We may update this policy from time to time. Material changes will be posted on this page with a revised
          "Last updated" date.
        </P>
      </Section>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage title="Terms of Service" testid="terms-page">
      <P>
        These Terms of Service ("Terms") govern your use of The Good Box self-service kiosks and admin portal at
        vend.thegoodbox.com (the "Service"), operated by {OPERATOR}. By using the Service you agree to these Terms.
      </P>

      <Section title="1. The Service">
        <P>
          The Good Box provides self-service retail kiosks that let you browse products, add them to a cart and pay by
          scanning a QR code, together with a secure portal for authorised administrators to manage products, stock
          and reporting.
        </P>
      </Section>

      <Section title="2. Purchases and payment">
        <P>
          All prices are shown at the kiosk and include VAT where applicable. Payment is taken at the time of purchase
          via our payment provider, Stripe. You are responsible for ensuring you take the items you have paid for.
        </P>
      </Section>

      <Section title="3. Refunds">
        <P>
          If you are charged incorrectly or a product is faulty, contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#3730A3] font-medium">{CONTACT_EMAIL}</a> with your
          transaction reference and we will investigate and, where appropriate, issue a refund.
        </P>
      </Section>

      <Section title="4. Administrator accounts">
        <P>
          Access to the admin portal is by invitation only and restricted to authorised users. You are responsible
          for keeping your login credentials secure and for all activity under your account. We may suspend accounts
          that breach these Terms or are used for unauthorised access.
        </P>
      </Section>

      <Section title="5. Acceptable use">
        <P>
          You agree not to misuse the Service, including attempting to gain unauthorised access, interfering with its
          operation, tampering with kiosks, or using it for any unlawful purpose.
        </P>
      </Section>

      <Section title="6. Availability">
        <P>
          We aim to keep the Service available but do not guarantee uninterrupted access. We may suspend or withdraw
          all or part of the Service for maintenance or operational reasons.
        </P>
      </Section>

      <Section title="7. Liability">
        <P>
          Nothing in these Terms excludes liability that cannot be excluded by law. Subject to that, we are not liable
          for indirect or consequential loss, and our total liability in connection with a purchase is limited to the
          amount you paid for that purchase.
        </P>
      </Section>

      <Section title="8. Governing law">
        <P>
          These Terms are governed by the laws of England and Wales, and the courts of England and Wales have
          exclusive jurisdiction.
        </P>
      </Section>

      <Section title="9. Contact">
        <P>
          Questions about these Terms? Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#3730A3] font-medium">{CONTACT_EMAIL}</a>.
        </P>
      </Section>
    </LegalPage>
  );
}
